import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Stack, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { SupplierBankAccountService } from '../../../services/paymentRun.service';
import { SupplierBankAccount, SupplierPaymentProfile as Profile } from '../../../interfaces/procurement/paymentRun.interface';
import { AuthService } from '../../../services/auth.service';
import { useToast } from '../../../context/ToastContext';
import ScrollableTable from '../../common/ScrollableTable';
import { ConfirmDialog } from '../../common/ConfirmDialog';

interface Props { supplierId: string; canManage: boolean }

const EMPTY = (supplierId: string): SupplierBankAccount => ({
  institutionId: AuthService.getSelectedInstitution()!!, supplierId, kind: 'BANK', bankName: '', branchName: '',
  routingNumber: '', accountNumber: '', accountName: '', mfsProvider: '', mfsWallet: '', defaultAccount: false, notes: '',
});

/** Supplier detail tab: bank / MFS payment details and early-payment discount terms (W3-P). */
export const SupplierPaymentProfile: React.FC<Props> = ({ supplierId, canManage }) => {
  const toast = useToast();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [pct, setPct] = useState<number | ''>('');
  const [days, setDays] = useState<number | ''>('');
  const [editing, setEditing] = useState<SupplierBankAccount | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<SupplierBankAccount | null>(null);

  const load = useCallback(async () => {
    try {
      const p = await SupplierBankAccountService.profile(supplierId);
      setProfile(p);
      setPct(p.earlyDiscountPct ?? '');
      setDays(p.earlyDiscountDays ?? '');
    } catch (e) {
      toast.showApiError(e, 'Failed to load payment details');
    }
  }, [supplierId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const saveTerms = async () => {
    setSaving(true);
    try {
      await SupplierBankAccountService.saveTerms(supplierId, pct === '' ? null : Number(pct), days === '' ? null : Number(days));
      toast.showSuccess('Early-payment terms saved');
      load();
    } catch (e) {
      toast.showApiError(e, 'Failed to save the early-payment terms');
    } finally {
      setSaving(false);
    }
  };

  const saveAccount = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      if (editing.id) await SupplierBankAccountService.update(editing.id, editing);
      else await SupplierBankAccountService.create(editing);
      toast.showSuccess('Payment details saved');
      setEditing(null);
      load();
    } catch (e) {
      toast.showApiError(e, 'Failed to save the payment details');
    } finally {
      setSaving(false);
    }
  };

  const makeDefault = async (a: SupplierBankAccount) => {
    try {
      await SupplierBankAccountService.makeDefault(a.id!);
      load();
    } catch (e) {
      toast.showApiError(e, 'Failed to set the default');
    }
  };

  const remove = async () => {
    if (!pendingDelete?.id) return;
    try {
      await SupplierBankAccountService.remove(pendingDelete.id);
      setPendingDelete(null);
      load();
    } catch (e) {
      toast.showApiError(e, 'Failed to remove the payment details');
    }
  };

  const set = (patch: Partial<SupplierBankAccount>) => setEditing(prev => (prev ? { ...prev, ...patch } : prev));
  const invalid = !editing || (editing.kind === 'BANK'
    ? !editing.bankName?.trim() || !editing.accountNumber?.trim()
    : !editing.mfsProvider?.trim() || !editing.mfsWallet?.trim());

  return (
    <Stack spacing={3}>
      <Paper data-help-section="paymentRun.supplierProfile.accounts" sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6">Bank and mobile-wallet details</Typography>
          {canManage && <Button data-help="paymentRun.supplierProfile.add" variant="contained" onClick={() => setEditing(EMPTY(supplierId))}>Add details</Button>}
        </Box>
        {profile && profile.accounts.length === 0 && (
          <Alert severity="info">No payment details yet. A bank-file payment run leaves this supplier out until a default bank account is added.</Alert>
        )}
        {profile && profile.accounts.length > 0 && (
          <ScrollableTable>
            <Table data-help-section="paymentRun.supplierProfile.table" size="small">
              <TableHead>
                <TableRow>
                  <TableCell data-help="paymentRun.supplierProfile.col.kind">Kind</TableCell><TableCell data-help="paymentRun.supplierProfile.col.bank">Bank / provider</TableCell><TableCell data-help="paymentRun.supplierProfile.col.branch">Branch</TableCell>
                  <TableCell data-help="paymentRun.supplierProfile.col.routing">Routing</TableCell><TableCell data-help="paymentRun.supplierProfile.col.account">Account / wallet</TableCell><TableCell data-help="paymentRun.supplierProfile.col.accountName">Account name</TableCell>
                  <TableCell data-help="paymentRun.supplierProfile.col.default">Default</TableCell>{canManage && <TableCell data-help="paymentRun.supplierProfile.col.actions" align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {profile.accounts.map(a => (
                  <TableRow key={a.id}>
                    <TableCell>{a.kind === 'BANK' ? 'Bank' : 'MFS'}</TableCell>
                    <TableCell>{a.kind === 'BANK' ? a.bankName : a.mfsProvider}</TableCell>
                    <TableCell>{a.branchName ?? '—'}</TableCell>
                    <TableCell>{a.routingNumber ?? '—'}</TableCell>
                    <TableCell>{a.kind === 'BANK' ? a.accountNumber : a.mfsWallet}</TableCell>
                    <TableCell>{a.accountName ?? '—'}</TableCell>
                    <TableCell>{a.defaultAccount ? <Chip size="small" color="primary" label="Default" /> : '—'}</TableCell>
                    {canManage && (
                      <TableCell align="right">
                        {!a.defaultAccount && <Button data-help="paymentRun.supplierProfile.action.makeDefault" size="small" onClick={() => makeDefault(a)}>Make default</Button>}
                        <Button data-help="paymentRun.supplierProfile.action.edit" size="small" onClick={() => setEditing({ ...a })}>Edit</Button>
                        <Button data-help="paymentRun.supplierProfile.action.remove" size="small" color="error" onClick={() => setPendingDelete(a)}>Remove</Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollableTable>
        )}
      </Paper>

      <Paper data-help-section="paymentRun.supplierProfile.discount" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Early-payment discount</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A bill paid within this many days of its bill date is offered this discount in a payment run.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField data-help="paymentRun.supplierProfile.discountPct" label="Discount %" type="number" size="small" value={pct} disabled={!canManage}
            onChange={e => setPct(e.target.value === '' ? '' : Number(e.target.value))}
            sx={{ flex: '1 1 160px', maxWidth: 220 }} inputProps={{ min: 0, max: 100, step: 0.01 }} />
          <TextField data-help="paymentRun.supplierProfile.discountDays" label="Within days of bill date" type="number" size="small" value={days} disabled={!canManage}
            onChange={e => setDays(e.target.value === '' ? '' : Number(e.target.value))}
            sx={{ flex: '1 1 160px', maxWidth: 220 }} inputProps={{ min: 0, max: 3650 }} />
          {canManage && <Button data-help="paymentRun.supplierProfile.saveTerms" variant="outlined" onClick={saveTerms} disabled={saving}>Save terms</Button>}
        </Box>
      </Paper>

      <Dialog data-help-page="procurement.bankAcctForm.page" open={!!editing} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editing?.id ? 'Edit payment details' : 'Add payment details'}</DialogTitle>
        <DialogContent>
          {editing && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField data-help="procurement.bankAcctForm.field.kind" select label="Kind" size="small" fullWidth value={editing.kind} disabled={!!editing.id}
                onChange={e => set({ kind: e.target.value as 'BANK' | 'MFS' })}>
                <MenuItem value="BANK">Bank account</MenuItem>
                <MenuItem value="MFS">Mobile wallet (bKash, Nagad, ...)</MenuItem>
              </TextField>
              {editing.kind === 'BANK' ? (
                <>
                  <TextField data-help="procurement.bankAcctForm.field.bankName" label="Bank name" size="small" fullWidth required value={editing.bankName ?? ''} onChange={e => set({ bankName: e.target.value })} />
                  <TextField data-help="procurement.bankAcctForm.field.branch" label="Branch" size="small" fullWidth value={editing.branchName ?? ''} onChange={e => set({ branchName: e.target.value })} />
                  <TextField data-help="procurement.bankAcctForm.field.routing" label="Routing number" size="small" fullWidth value={editing.routingNumber ?? ''} onChange={e => set({ routingNumber: e.target.value })} />
                  <TextField data-help="procurement.bankAcctForm.field.accountNo" label="Account number" size="small" fullWidth required value={editing.accountNumber ?? ''} onChange={e => set({ accountNumber: e.target.value })} />
                  <TextField data-help="procurement.bankAcctForm.field.accountName" label="Account name" size="small" fullWidth value={editing.accountName ?? ''} onChange={e => set({ accountName: e.target.value })} />
                </>
              ) : (
                <>
                  <TextField data-help="procurement.bankAcctForm.field.provider" label="Provider" size="small" fullWidth required value={editing.mfsProvider ?? ''} onChange={e => set({ mfsProvider: e.target.value })} />
                  <TextField data-help="procurement.bankAcctForm.field.wallet" label="Wallet number" size="small" fullWidth required value={editing.mfsWallet ?? ''} onChange={e => set({ mfsWallet: e.target.value })} />
                </>
              )}
              <TextField data-help="procurement.bankAcctForm.field.notes" label="Notes" size="small" fullWidth value={editing.notes ?? ''} onChange={e => set({ notes: e.target.value })} />
              <TextField data-help="procurement.bankAcctForm.field.default" select label="Use as default" size="small" fullWidth value={editing.defaultAccount ? 'yes' : 'no'}
                onChange={e => set({ defaultAccount: e.target.value === 'yes' })}>
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="yes">Yes, default for this kind</MenuItem>
              </TextField>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button data-help="procurement.bankAcctForm.cancel" onClick={() => setEditing(null)}>Cancel</Button>
          <Button data-help="procurement.bankAcctForm.save" variant="contained" onClick={saveAccount} disabled={saving || invalid}>Save</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog open={!!pendingDelete} title="Remove payment details"
        message="Remove these payment details? Past payment runs are not affected."
        confirmLabel="Remove" confirmColor="error" onCancel={() => setPendingDelete(null)} onConfirm={remove} />
    </Stack>
  );
};

export default SupplierPaymentProfile;
